"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { cn } from "@/lib/cn";
import { explorerTxUrl } from "@/lib/explorers";
import { EASE } from "@/lib/motion";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Panel } from "@/components/ui/primitives";

/* The building blocks the account views share, in their own file so the
   rewards history — which is also mounted on the /rewards page — can use the
   same table, pills and verify cell as the tabs without importing the tabs. */

/* The kit's stat tile, the one every account view opens with: a 36px icon
   badge, an upper-case label and the figure in the H5 title face. */
export function StatTile({ icon, label, value, index = 0 }: { icon: IconName; label: string; value: string; index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, ease: EASE, delay: index * 0.06 }}
      className="surface-sheen flex min-w-0 flex-col gap-[12px] rounded-xl border border-border p-[16px]"
    >
      <span className="grid h-[36px] w-[36px] place-items-center rounded-lg bg-brand-bold/10 text-brand-bold">
        <Icon name={icon} size={20} />
      </span>
      <div className="flex min-w-0 flex-col gap-[4px]">
        <p className="text-[12px] font-bold uppercase leading-[1.6] text-fg-subtle">{label}</p>
        <p className="truncate text-h6 font-extrabold lg:text-h5">{value}</p>
      </div>
    </motion.div>
  );
}

/* The kit's text-banner: a headline, a line under it and one way onward. */
export function Banner({ title, body, cta, href }: { title: string; body: string; cta: string; href: string }) {
  return (
    <Panel className="surface-sheen flex flex-col gap-[16px] p-[24px] sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 flex-col gap-[4px]">
        <h2 className="text-h6 font-extrabold lg:text-h5">{title}</h2>
        <p className="text-[14px] font-medium leading-[1.6] text-fg-subtle">{body}</p>
      </div>
      <Link
        href={href}
        className="inline-flex h-[42px] shrink-0 items-center justify-center gap-[8px] rounded-lg bg-brand px-[16px] text-[14px] font-bold capitalize leading-[1.6] tracking-[0.28px] text-fg-on-primary transition-colors duration-200 hover:bg-brand-bold"
      >
        {cta}
        <Icon name="arrow-right" size={16} />
      </Link>
    </Panel>
  );
}

/* The kit's filter pills, which sit under a block's header on the left. */
export function Pills<T extends string>({ options, value, onChange }: { options: readonly T[]; value: T; onChange: (next: T) => void }) {
  return (
    <div className="scrollbar-none flex gap-[8px] overflow-x-auto px-[16px] pb-[12px] pt-[16px]">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={cn(
            "h-[40px] shrink-0 rounded-rounded px-[20px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
            value === option ? "bg-brand text-fg-on-primary" : "bg-elevation-2 text-fg-subtle hover:text-fg",
          )}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export const TABLE_HEAD = "h-[42px] border-b border-border bg-elevation-2 text-[12px] font-bold leading-[1.6] text-fg-subtle";

export const TABLE_ROW =
  "h-[51px] border-b border-border bg-elevation-2 text-[12px] font-bold leading-[1.6] transition-colors last:border-0 hover:bg-elevation-3";

/* The VERIFY cell as the live tables draw it: the short hash in the brand
   accent, linking out to the explorer, or a dash when there is no hash or no
   chain to resolve it on. */
export function VerifyLink({ hash, fullHash, chainId }: { hash: string; fullHash?: string; chainId?: number }) {
  const href = fullHash ? explorerTxUrl(chainId, fullHash) : null;

  if (!href) {
    return <span className="font-medium text-fg-subtle">&mdash;</span>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label="Open in block explorer"
      className="group inline-flex items-center justify-end gap-[6px]"
    >
      <span className="text-brand-fg-accent transition-colors group-hover:text-fg">{hash}</span>
      <Icon name="external" size={16} className="shrink-0 text-fg-subtle transition-colors group-hover:text-fg" />
    </a>
  );
}

